// Public article — renders one blog post (Markdown body).
import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Clock, Calendar } from "lucide-react";
import Layout from "@/components/Layout";
import SEO from "@/components/SEO";
import GeoVinumMap from "@/components/GeoVinumMap";
import EbookDownloadForm from "@/components/EbookDownloadForm";
import AtlasBlocksSection from "@/components/AtlasBlocksSection";
import ReadingProgressBar from "@/components/ReadingProgressBar";
import { useLang, useT } from "@/lib/i18n";
import { apiFetch } from "@/lib/api";

const DATE_LOCALES = { en: "en-GB", fr: "fr-FR", es: "es-ES", pt: "pt-PT" };

function renderMarkdown(md) {
  if (!md) return "";
  let html = String(md);
  html = html.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
  html = html.replace(/^###### (.+)$/gm, "<h6>$1</h6>");
  html = html.replace(/^##### (.+)$/gm, "<h5>$1</h5>");
  html = html.replace(/^#### (.+)$/gm, "<h4>$1</h4>");
  html = html.replace(/^### (.+)$/gm, "<h3>$1</h3>");
  html = html.replace(/^## (.+)$/gm, "<h2>$1</h2>");
  html = html.replace(/^# (.+)$/gm, "<h1>$1</h1>");
  html = html.replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>");
  html = html.replace(/\*([^*]+)\*/g, "<em>$1</em>");
  html = html.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank" rel="noreferrer">$1</a>');
  html = html.replace(/!\[([^\]]*)\]\(([^)]+)\)/g, '<img src="$2" alt="$1" />');
  // Markdown tables: |a|b|\n|---|---|\n|x|y|… → <table>
  html = html.replace(
    /(^\|[^\n]+\|\n\|[\s:|-]+\|\n(?:\|[^\n]+\|\n?)+)/gm,
    (block) => {
      const lines = block.trim().split("\n").filter(Boolean);
      if (lines.length < 2) return block;
      const split = (l) => l.replace(/^\||\|$/g, "").split("|").map((c) => c.trim());
      const head = split(lines[0]);
      const body = lines.slice(2).map(split);
      const th = head.map((c) => `<th>${c}</th>`).join("");
      const trs = body.map((r) => `<tr>${r.map((c) => `<td>${c}</td>`).join("")}</tr>`).join("");
      return `<table><thead><tr>${th}</tr></thead><tbody>${trs}</tbody></table>`;
    },
  );
  html = html.replace(/^(?:- (.+)\n?)+/gm, (m) => {
    const items = m.trim().split("\n").map((l) => l.replace(/^- /, "").trim()).map((tx) => `<li>${tx}</li>`).join("");
    return `<ul>${items}</ul>`;
  });
  html = html.split(/\n{2,}/).map((p) => {
    const tx = p.trim();
    if (!tx) return "";
    if (/^<(h[1-6]|ul|ol|blockquote|img|pre|table)/.test(tx)) return tx;
    return `<p>${tx.replace(/\n/g, "<br/>")}</p>`;
  }).join("\n");
  return html;
}

const BlogArticlePage = () => {
  const t = useT();
  const lang = useLang();
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  // If GeoVinumMap can't load (token failure, empty appellation, etc.) we
  // collapse the whole aside so the article reflows to full width.
  const [mapAvailable, setMapAvailable] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setMapAvailable(true);  // reset when navigating between articles
    (async () => {
      const { ok, data } = await apiFetch(`/api/blog/articles/${slug}`);
      if (!cancelled) {
        setData(ok ? data : { error: true });
        setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [slug]);

  if (loading) return <Layout testId="blog-article-loading"><div className="min-h-[60vh] flex items-center justify-center text-[color:var(--gv-ink)]/55">{t("blogP.article.loading")}</div></Layout>;
  if (!data || data.error) {
    return (
      <Layout testId="blog-article-error">
        <div className="min-h-[60vh] flex items-center justify-center px-6">
          <div className="text-center">
            <div className="font-display text-2xl mb-3">{t("blogP.article.not_found")}</div>
            <Link to="/blog" className="btn-primary">{t("blogP.article.back")} <ArrowLeft size={14} className="rotate-180" /></Link>
          </div>
        </div>
      </Layout>
    );
  }

  const dateLocale = DATE_LOCALES[lang] || "en-GB";
  const wantsMap = Boolean(data.geovinum_enabled && data.appellation);
  const showMap = wantsMap && mapAvailable;
  // Ebook lead-capture: only when category=ebook AND a PDF is actually attached.
  // The form replaces the geoVINUM aside (mutually exclusive — an ebook
  // article describes a downloadable resource, not an appellation).
  const showEbook = data.category === "ebook" && Boolean(data.ebook_pdf_filename);
  const showSideRail = showMap || showEbook;

  return (
    <Layout testId={`blog-article-${slug}`}>
      <ReadingProgressBar targetSelector={`[data-testid="blog-article-${slug}"] article`} />
      <SEO
        title={`${data.title} | geoVINUM Blog`}
        description={data.excerpt?.slice(0, 160)}
        image={data.cover_image_url}
      />

      {/* Ebook articles get their PDF cover image embedded inside the body
          column (next to the form) — see the article column below.
          All other categories: hero cover image is intentionally NOT rendered
          here. The interactive geoVINUM map (when an appellation is set)
          carries the visual context; on production, the static thumbnail URL
          would 404 outside of preview anyway. We keep `cover_image_url` on
          the SEO/OpenGraph tags above so social shares still get a card. */}

      <div className={showSideRail ? "max-w-7xl mx-auto px-6 lg:px-10 py-12" : "max-w-3xl mx-auto px-6 lg:px-10 py-12"}>
        <Link to="/blog" className="inline-flex items-center gap-1.5 text-[13px] text-[color:var(--gv-ink)]/65 hover:text-[color:var(--gv-pink)] mb-6"><ArrowLeft size={13} /> {t("blogP.article.back")}</Link>

        <div className={showSideRail ? "grid grid-cols-1 lg:grid-cols-12 gap-10" : ""}>
          {/* On desktop with map: left column = sticky map; on mobile: map appears above the text (we duplicate the placement so it stacks first via order). */}
          {showMap && (
            <aside className="lg:col-span-5 order-1 lg:order-1" data-testid="article-geovinum-aside">
              <div className="lg:sticky lg:top-24">
                <GeoVinumMap appellation={data.appellation} onUnavailable={() => setMapAvailable(false)} />
                <div className="text-[11px] text-[color:var(--gv-ink)]/50 mt-2 px-1">
                  Carte interactive de l&apos;appellation <span className="text-[color:var(--gv-ink)]/75">{data.appellation}</span> · geoVINUM
                </div>
              </div>
            </aside>
          )}
          {showEbook && (
            <aside className="lg:col-span-5 order-1 lg:order-1" data-testid="article-ebook-aside">
              <div className="lg:sticky lg:top-24">
                <EbookDownloadForm
                  slug={slug}
                  title={data.title}
                  lang={lang}
                  pages={data.ebook_pages || 0}
                  sizeBytes={data.ebook_pdf_size_bytes || 0}
                  filename={data.ebook_pdf_filename}
                />
              </div>
            </aside>
          )}

          <article className={showSideRail ? "lg:col-span-7 order-2 lg:order-2" : ""}>
            {showEbook && data.cover_image_url && (
              <div className="mb-6 rounded-[8px] overflow-hidden border border-[color:var(--gv-line)] bg-[color:var(--gv-mist)] shadow-sm" data-testid="article-ebook-cover">
                <img
                  src={data.cover_image_url}
                  alt={`${t("ebookF.eyebrow")} — ${data.title}`}
                  className="w-full h-auto block"
                  loading="lazy"
                />
              </div>
            )}
            <div className="text-[11px] uppercase tracking-widest text-[color:var(--gv-pink)] mb-3" data-testid="article-category">{data.category?.replace("_", " ")}</div>
            <h1 className="font-display text-4xl md:text-5xl text-[color:var(--gv-ink)] leading-[1.1] mb-4" data-testid="article-title">{data.title}</h1>
            {data.excerpt && <p className="text-[16px] text-[color:var(--gv-ink)]/75 leading-relaxed mb-6" data-testid="article-excerpt">{data.excerpt}</p>}

            <div className="flex items-center gap-3 text-[12px] text-[color:var(--gv-ink)]/55 mb-10 pb-6 border-b border-[color:var(--gv-line)]">
              {data.author && <span>{t("blogP.article.by")} {data.author}</span>}
              {data.published_at && <span className="flex items-center gap-1"><Calendar size={11} /> {new Date(data.published_at).toLocaleDateString(dateLocale, { day: "numeric", month: "long", year: "numeric" })}</span>}
              <span className="flex items-center gap-1"><Clock size={11} /> {data.read_minutes || 1} {t("blogP.article.read_time")}</span>
            </div>

            <div
              className="article-body article-content text-[15.5px] leading-[1.75] text-[color:var(--gv-ink)]/90"
              dangerouslySetInnerHTML={{ __html: renderMarkdown(data.body_markdown) }}
              data-testid="article-body"
            />

            {/* Atlas — public blocks from the enriched fiche.
                Auto-hidden when the article has no linked appellation/cépage
                OR when upstream returns zero public blocks. */}
            <AtlasBlocksSection
              appellation={data.appellation}
              cepage={data.cepage}
              producer={data.producer}
              lang={data.language || lang}
            />

            {/* Cross-sell */}
            <div className="mt-14 pt-10 border-t border-[color:var(--gv-line)]" data-testid="article-crosslinks">
              <div className="text-[11px] uppercase tracking-widest text-[color:var(--gv-pink)] mb-3">{t("blogP.article.cross.eyebrow")}</div>
              <h3 className="font-display text-xl text-[color:var(--gv-ink)] mb-5">{t("blogP.article.cross.title")}</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Link
                  to={data.appellation ? `/shop?q=${encodeURIComponent(data.appellation)}` : "/shop"}
                  className="group block bg-[color:var(--gv-mist)] border border-[color:var(--gv-line)] rounded-[5px] p-4 hover:border-[color:var(--gv-pink)] transition-colors"
                  data-testid="article-crosslink-shop"
                >
                  <div className="text-[10px] uppercase tracking-widest text-[color:var(--gv-pink)] mb-1.5">{t("blogP.article.cross.shop_label")}</div>
                  <div className="font-display text-[14.5px] text-[color:var(--gv-ink)] group-hover:text-[color:var(--gv-pink)]">
                    {data.appellation ? `Voir la carte ${data.appellation}` : t("blogP.article.cross.shop_title")}
                  </div>
                  <div className="text-[12px] text-[color:var(--gv-ink)]/60 mt-1">{t("blogP.article.cross.shop_desc")}</div>
                </Link>
                <Link to="/geovinum-ai" className="group block bg-[color:var(--gv-mist)] border border-[color:var(--gv-line)] rounded-[5px] p-4 hover:border-[color:var(--gv-pink)] transition-colors" data-testid="article-crosslink-app">
                  <div className="text-[10px] uppercase tracking-widest text-[color:var(--gv-pink)] mb-1.5">geoVINUM APP</div>
                  <div className="font-display text-[14.5px] text-[color:var(--gv-ink)] group-hover:text-[color:var(--gv-pink)]">
                    {data.appellation ? `Explorer ${data.appellation} dans l'app` : "Explorer, apprendre & déguster"}
                  </div>
                  <div className="text-[12px] text-[color:var(--gv-ink)]/60 mt-1">Cartes, fiches, quiz et IA — tout dans une seule app.</div>
                </Link>
              </div>
            </div>
          </article>
        </div>
      </div>
    </Layout>
  );
};

export default BlogArticlePage;
